import { CryptoError, generatePassword, generateSalt, parseRecoveryKey, zeroBytes } from "@passwordflow/crypto";
import { completeRecovery, fetchSalt, login, reauthWithPassword, setInitialPassword, setupInstance, startRecovery } from "../api/auth.js";
import type { LoginResult, RecoveryGrant, RecoveryStart, SecondFactorOutcome } from "../api/auth.js";
import { changeMasterPassword, fetchAccountKeys, saveRecoveryKit, storeAccountKeys } from "../api/account.js";
import { ApiClientError } from "../api/http.js";
import { requestExport } from "../api/policies.js";
import { bytesToB64 } from "../b64.js";
import { ClientError } from "../cryptoClient.js";
import type { CryptoClient } from "../cryptoClient.js";
import { loadVault } from "../data/vault.js";
import { buildVaultExport } from "../lib/vaultExport.js";
import type { VaultExport } from "../lib/vaultExport.js";

export type AuthStep =
  | { readonly kind: "authenticated" }
  /** passwordChange: the password was a one-time password; after the factor a new one must be set. */
  | { readonly kind: "second-factor"; readonly ticket: string; readonly passwordChange: boolean }
  /** recovered: an admin reset the account; its keys are already loaded and get re-sealed under the new password. */
  | { readonly kind: "set-password"; readonly ticket: string; readonly recovered: boolean };

export const INVALID_CREDENTIALS = "E-Mail oder Master-Passwort ist falsch.";

/** Thrown when a step fails for a reason the user can act on. The message is user-facing German. */
export type AuthFlowErrorCode = "vault-unreadable" | "incomplete" | "recovery-key" | "admin-recovery" | "passkey-prf" | "passkey-account";

export class AuthFlowError extends Error {
  public readonly code: AuthFlowErrorCode;

  public constructor(code: AuthFlowErrorCode, message: string) {
    super(message);
    this.name = "AuthFlowError";
    this.code = code;
  }
}

function stepFrom(result: LoginResult): AuthStep {
  if (result.passwordChangeRequired === true) {
    if (result.ticket === undefined) {
      throw new AuthFlowError("incomplete", "Anmeldung unvollständig. Bitte erneut versuchen.");
    }
    return { kind: "set-password", ticket: result.ticket, recovered: result.keyEncB64 !== undefined };
  }
  if (!result.twoFactorRequired) {
    return { kind: "authenticated" };
  }
  if (result.ticket === undefined) {
    throw new AuthFlowError("incomplete", "Anmeldung unvollständig. Bitte erneut versuchen.");
  }
  return { kind: "second-factor", ticket: result.ticket, passwordChange: result.passwordChangeAfterTwoFactor === true };
}

/** Admin recovery: the account keys are sealed under the one-time password the worker just derived. */
async function openRecoveredKeys(client: CryptoClient, keyEncB64: string): Promise<void> {
  try {
    await client.openAccountKeys(keyEncB64);
  } catch {
    throw new AuthFlowError("vault-unreadable", "Die Kontoschlüssel lassen sich mit dem Einmalpasswort nicht öffnen. Bitte deinen Administrator, die Wiederherstellung zu wiederholen.");
  }
}

/** The step after the second factor of a sign-in passed: open the vault, or set the new master password. */
export async function afterSecondFactor(client: CryptoClient, outcome: SecondFactorOutcome): Promise<AuthStep> {
  if (outcome.kind === "session") {
    await openAccountKeys(client);
    return { kind: "authenticated" };
  }
  if (outcome.kind === "password-change") {
    if (outcome.grant.keyEncB64 !== undefined) {
      await openRecoveredKeys(client, outcome.grant.keyEncB64);
    }
    return { kind: "set-password", ticket: outcome.grant.ticket, recovered: outcome.grant.keyEncB64 !== undefined };
  }
  throw new AuthFlowError("incomplete", "Anmeldung unvollständig. Bitte erneut versuchen.");
}

/**
 * Loads the account keys into the worker once the session stands. An account from before the
 * account keys adopts its password-derived keys and stores them sealed, so nothing is re-encrypted.
 */
export async function openAccountKeys(client: CryptoClient): Promise<void> {
  const state = await fetchAccountKeys();
  let adopted: string | undefined;
  try {
    adopted = await client.openAccountKeys(state.keyEncB64);
  } catch {
    throw new AuthFlowError("vault-unreadable", "Die Kontoschlüssel lassen sich nicht öffnen. Bitte erneut anmelden.");
  }
  if (adopted !== undefined) {
    await storeAccountKeys(adopted);
  }
}

/** Derives the keys in the worker, proves the password to the server, then loads the account keys. */
export async function signIn(client: CryptoClient, email: string, password: string): Promise<AuthStep> {
  const salt = await fetchSalt(email);
  const authKeyB64 = await client.unlock(password, salt.saltB64, email);
  const result = await login(email, authKeyB64);
  const step = stepFrom(result);
  if (step.kind === "authenticated") {
    await openAccountKeys(client);
  }
  if (step.kind === "set-password" && result.keyEncB64 !== undefined) {
    await openRecoveredKeys(client, result.keyEncB64);
  }
  return step;
}

/** First start of a fresh instance: creates the admin account with the code from the server log. */
export async function setupAdmin(client: CryptoClient, code: string, email: string, password: string): Promise<void> {
  const saltB64 = bytesToB64(generateSalt(16));
  const authKeyB64 = await client.unlock(password, saltB64, email);
  const keyEncB64 = await client.createAccountKeys();
  await setupInstance(code, email, saltB64, authKeyB64, keyEncB64);
}

/**
 * Replaces the one-time password with the user's own master password. Fresh salt, so the new
 * keys share nothing with the one the admin knew. A new account gets fresh account keys; after an
 * admin recovery the existing ones (loaded by signIn) are re-sealed, so every item stays readable.
 */
export async function chooseInitialPassword(client: CryptoClient, email: string, ticket: string, password: string, recovered: boolean = false): Promise<void> {
  const saltB64 = bytesToB64(generateSalt(16));
  if (recovered) {
    const sealed = await client.sealAccountKeysFor(password, saltB64, email);
    await setInitialPassword(ticket, saltB64, sealed.authKeyB64, sealed.keyEncB64, true);
    return;
  }
  const authKeyB64 = await client.unlock(password, saltB64, email);
  const keyEncB64 = await client.createAccountKeys();
  await setInitialPassword(ticket, saltB64, authKeyB64, keyEncB64);
}

/**
 * Re-proves the password for an expired session. Uses deriveAuth, so a wrong password
 * never replaces the vault keys that are still loaded in the worker.
 */
export async function reauthenticate(client: CryptoClient, email: string, password: string): Promise<AuthStep> {
  const salt = await fetchSalt(email);
  const authKeyB64 = await client.deriveAuthKey(password, salt.saltB64, email);
  return stepFrom(await login(email, authKeyB64));
}

/** Step-up check with the master password, for changes the server only allows right after a check. */
export async function confirmMasterPassword(client: CryptoClient, email: string, password: string): Promise<void> {
  const salt = await fetchSalt(email);
  await reauthWithPassword(await client.deriveAuthKey(password, salt.saltB64, email));
}

/**
 * Changes the master password. The account keys stay the same and are only sealed under the new
 * password, so items, team keys and the emergency kit are untouched. A failure leaves the old password valid.
 */
export async function changePassword(client: CryptoClient, email: string, current: string, next: string): Promise<void> {
  const salt = await fetchSalt(email);
  const currentAuthKeyB64 = await client.deriveAuthKey(current, salt.saltB64, email);
  const saltB64 = bytesToB64(generateSalt(16));
  const sealed = await client.sealAccountKeysFor(next, saltB64, email);
  await changeMasterPassword({ currentAuthKeyB64, saltB64, authKeyB64: sealed.authKeyB64, keyEncB64: sealed.keyEncB64 });
}

export interface CreatedKit {
  /** Shown once and printed into the emergency kit; the server never sees it. */
  readonly recoveryKey: string;
  readonly createdAt: number;
}

/** Creates or replaces the emergency kit. Needs the master password. */
export async function createRecoveryKit(client: CryptoClient, email: string, password: string): Promise<CreatedKit> {
  const salt = await fetchSalt(email);
  const currentAuthKeyB64 = await client.deriveAuthKey(password, salt.saltB64, email);
  const kit = await client.createRecoveryKit();
  const createdAt = await saveRecoveryKit({ currentAuthKeyB64, keyEncB64: kit.keyEncB64, authKeyB64: kit.authKeyB64 });
  return { recoveryKey: kit.recoveryKey, createdAt };
}

/**
 * Decrypted copy of the personal vault as a JSON file. The server first checks the master
 * password and the export policy, and records the export; the plaintext never leaves this browser.
 */
export async function exportVault(client: CryptoClient, email: string, password: string, now: Date): Promise<VaultExport> {
  const salt = await fetchSalt(email);
  const currentAuthKeyB64 = await client.deriveAuthKey(password, salt.saltB64, email);
  await requestExport(currentAuthKeyB64);
  const vault = await loadVault(client);
  return buildVaultExport(vault.entries, vault.skipped, now);
}

/** Checks a typed or scanned recovery key before anything is sent. Returns the user-facing problem, if any. */
export function recoveryKeyProblem(input: string): string | undefined {
  if (input.trim() === "") {
    return "Bitte gib den Wiederherstellungsschlüssel ein.";
  }
  try {
    zeroBytes(parseRecoveryKey(input));
    return undefined;
  } catch (err: unknown) {
    if (err instanceof CryptoError) {
      return "Der Schlüssel ist unvollständig oder enthält einen Tippfehler. Prüfe ihn mit deinem Notfall-Kit.";
    }
    throw err;
  }
}

/** Forgotten master password, step 1: proves the recovery key. A second factor may follow. */
export async function beginRecovery(client: CryptoClient, email: string, recoveryKey: string): Promise<RecoveryStart> {
  const problem = recoveryKeyProblem(recoveryKey);
  if (problem !== undefined) {
    throw new AuthFlowError("recovery-key", problem);
  }
  const recoveryAuthKeyB64 = await client.recoveryProof(recoveryKey);
  return startRecovery(email, recoveryAuthKeyB64);
}

/**
 * Forgotten master password, step 2: opens the account keys with the recovery key, seals them under
 * the new password and a new recovery key, and signs in. Returns the new recovery key for the new kit.
 */
export async function finishRecovery(
  client: CryptoClient,
  email: string,
  recoveryKey: string,
  grant: RecoveryGrant,
  password: string
): Promise<string> {
  try {
    await client.recoveryOpen(recoveryKey, grant.recoveryKeyEncB64);
  } catch {
    throw new AuthFlowError("vault-unreadable", "Der Wiederherstellungsschlüssel öffnet die Kontoschlüssel nicht. Wende dich an deinen Administrator.");
  }
  const saltB64 = bytesToB64(generateSalt(16));
  const sealed = await client.sealAccountKeysFor(password, saltB64, email);
  const kit = await client.createRecoveryKit();
  await completeRecovery({
    ticket: grant.ticket,
    saltB64,
    authKeyB64: sealed.authKeyB64,
    keyEncB64: sealed.keyEncB64,
    recovery: { keyEncB64: kit.keyEncB64, authKeyB64: kit.authKeyB64 }
  });
  return kit.recoveryKey;
}

/** Readable one-time password for a new account: no look-alike characters, no symbols, ~114 bits. */
export function generateTempPassword(): string {
  return generatePassword({ length: 20, sets: ["lower", "upper", "digits"] });
}

/** Login proof for a one-time password. Uses deriveAuth, so the admin's own vault keys stay untouched. */
export async function tempCredentials(client: CryptoClient, email: string, password: string): Promise<{ saltB64: string; authKeyB64: string }> {
  const saltB64 = bytesToB64(generateSalt(16));
  const authKeyB64 = await client.deriveAuthKey(password, saltB64, email);
  return { saltB64, authKeyB64 };
}

/** True when the user closed or timed out the browser's passkey prompt. */
export function isPasskeyCancel(err: unknown): boolean {
  return err instanceof Error && (err.name === "NotAllowedError" || err.name === "AbortError");
}

/** German, user-facing messages. Unknown emails and wrong passwords share one message (no enumeration). */
export function describeAuthError(err: unknown): string {
  if (err instanceof AuthFlowError) {
    return err.message;
  }
  if (err instanceof ApiClientError) {
    if (err.status === 401 || err.status === 404) {
      return err.message.startsWith("Die ") ? err.message : INVALID_CREDENTIALS;
    }
    if (err.status === 409) {
      return err.message;
    }
    if (err.status === 429) {
      return "Zu viele Versuche. Bitte warte einen Moment.";
    }
    if (err.status === 400 || err.status === 403) {
      return err.message;
    }
    return "Der Server meldet einen Fehler. Bitte erneut versuchen.";
  }
  if (err instanceof ClientError) {
    return "Der Tresor konnte nicht entsperrt werden. Bitte erneut versuchen.";
  }
  if (isPasskeyCancel(err)) {
    return "Die Passkey-Abfrage wurde abgebrochen.";
  }
  if (err instanceof TypeError) {
    return "Der Server ist nicht erreichbar.";
  }
  return "Unerwarteter Fehler. Bitte erneut versuchen.";
}
